import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash, Lightning, ListChecks, X, TestTube } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  listAutomations, createAutomation, updateAutomation, deleteAutomation,
  testAutomation, listAutomationRuns, getAutomationPlaceholders,
  getOrders,
} from '../../services/api';

const STATUSES = [
  'new_order', 'order_submitted', 'deposit_paid', 'ready_to_load',
  'loaded', 'shipped', 'arrived_alg', 'customs_clearance', 'delivered', 'cancelled'
];

const FIELD_OPTIONS = [
  { value: 'order.status', label: 'Order status' },
  { value: 'order.payment_percent', label: 'Payment %' },
  { value: 'order.total_selling_price', label: 'Total selling price' },
  { value: 'order.total_paid', label: 'Total paid' },
  { value: 'client.client_type', label: 'Client type (individual/dealer)' },
  { value: 'car.brand', label: 'Car brand' },
  { value: 'car.model', label: 'Car model' },
  { value: 'car.color', label: 'Car color' },
  { value: 'days_since_shipped', label: 'Days since shipped' },
  { value: 'days_since_created', label: 'Days since order created' },
];

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in (comma list)' },
];

const emptyRule = () => ({
  name: '',
  enabled: true,
  trigger: { type: 'status_change', config: { to_status: 'shipped', any_change: false } },
  conditions: { op: 'AND', items: [] },
  recipients: [{ type: 'client_email' }],
  channels: ['email'],
  templates: { subject_fr: '', body_fr: '', subject_en: '', body_en: '' },
  recurrence: { mode: 'once', interval_days: 7 },
});

const RuleRow = ({ rule, onEdit, onDelete, onToggle, onTest }) => (
  <tr className="border-t border-border" data-testid={`automation-row-${rule.id}`}>
    <td className="p-3 align-top">
      <Switch checked={rule.enabled} onCheckedChange={(v) => onToggle(rule, v)} data-testid={`automation-toggle-${rule.id}`} />
    </td>
    <td className="p-3 align-top">
      <p className="font-medium text-sm">{rule.name}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{describeTrigger(rule.trigger)}</p>
    </td>
    <td className="p-3 align-top text-xs">
      {(rule.recipients || []).map((r, i) => (
        <span key={i} className="inline-block bg-secondary px-1.5 py-0.5 rounded mr-1 mb-1">
          {describeRecipient(r)}
        </span>
      ))}
    </td>
    <td className="p-3 align-top text-xs">
      {(rule.channels || []).map((c) => (
        <span key={c} className={`inline-block px-1.5 py-0.5 rounded mr-1 mb-1 ${c === 'email' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'}`}>{c}</span>
      ))}
    </td>
    <td className="p-3 align-top text-xs">
      {rule.recurrence?.mode === 'recurring' ? `every ${rule.recurrence.interval_days}d` : 'once'}
    </td>
    <td className="p-3 align-top text-right whitespace-nowrap">
      <Button variant="ghost" size="icon" onClick={() => onTest(rule)} title="Test fire" data-testid={`automation-test-${rule.id}`}>
        <TestTube className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="icon" onClick={() => onEdit(rule)} data-testid={`automation-edit-${rule.id}`}>
        <Pencil className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onDelete(rule)} data-testid={`automation-delete-${rule.id}`}>
        <Trash className="w-4 h-4" />
      </Button>
    </td>
  </tr>
);

function describeTrigger(t) {
  if (!t) return '';
  if (t.type === 'status_change') {
    if (t.config?.any_change) return 'When any status changes';
    return `When status becomes "${t.config?.to_status}"`;
  }
  if (t.type === 'time_after') {
    return `${t.config?.days || 0}d after ${t.config?.anchor === 'order_created' ? 'order creation' : `status "${t.config?.anchor_status}"`}`;
  }
  if (t.type === 'payment_state') {
    const s = t.config?.state || 'fully_paid';
    return `When payment is ${s}${s === 'partial' ? ` ≥ ${t.config?.threshold || 0}%` : ''}`;
  }
  if (t.type === 'document_signed') {
    const doc = t.config?.document || 'any';
    const by = t.config?.signed_by || 'any';
    return `When ${doc === 'any' ? 'any document' : doc} is signed${by !== 'any' ? ` by ${by}` : ''}`;
  }
  if (t.type === 'container_event') {
    const s = t.config?.t49_status || 'any';
    return s === 'any' ? 'On any container event' : `Container: ${s}`;
  }
  return t.type;
}

function describeRecipient(r) {
  if (r.type === 'client_email') return 'Client (email)';
  if (r.type === 'client_telegram') return 'Client (telegram)';
  if (r.type === 'assigned_agent') return 'Assigned agent';
  if (r.type === 'role') return `Role: ${r.value}`;
  if (r.type === 'specific_employee') return `Employee: ${r.value?.slice(0, 8)}…`;
  if (r.type === 'custom_email') return r.value;
  return r.type;
}

const AutomationsPage = () => {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('rules');
  const [placeholders, setPlaceholders] = useState([]);
  const [testDialog, setTestDialog] = useState(null); // { rule, orders }
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    const [r, runRes, ph] = await Promise.all([
      listAutomations(),
      listAutomationRuns(),
      getAutomationPlaceholders(),
    ]);
    setRules(r.data || []);
    setRuns(runRes.data || []);
    setPlaceholders(ph.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (rule) => {
    try {
      if (rule.id) await updateAutomation(rule.id, rule);
      else await createAutomation(rule);
      toast.success('Rule saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete automation "${rule.name}"?`)) return;
    await deleteAutomation(rule.id);
    toast.success('Deleted');
    load();
  };

  const handleToggle = async (rule, enabled) => {
    await updateAutomation(rule.id, { ...rule, enabled });
    load();
  };

  const openTest = async (rule) => {
    if (!orders.length) {
      try {
        const r = await getOrders({ limit: 100 });
        setOrders(r.data?.orders || []);
      } catch {/* empty */}
    }
    setTestDialog({ rule });
  };

  const runTest = async (orderId) => {
    if (!orderId) return;
    try {
      const r = await testAutomation(testDialog.rule.id, orderId);
      const summary = r.data.summary || r.data.run || {};
      const ok = summary.ok_count || 0;
      const errors = summary.error_count || 0;
      const forced = r.data.forced_recipient;
      if (ok > 0 && errors === 0) {
        toast.success(`Test fired instantly — ${ok} message${ok === 1 ? '' : 's'} delivered${forced ? ` (incl. you @ ${forced.channel === 'email' ? forced.target : 'Telegram'})` : ''}`);
      } else if (ok > 0 && errors > 0) {
        toast.warning(`Test fired — ${ok} sent, ${errors} failed. Check Run log.`);
      } else if (errors > 0) {
        toast.error(`Test failed — ${errors} send error${errors === 1 ? '' : 's'}. Check Run log for details.`);
      } else {
        toast.warning('Test ran but no recipients resolved. Add an email/Telegram channel + at least one recipient on the rule.');
      }
      setTestDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="automations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Lightning className="w-7 h-7" weight="fill" /> Automations
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Custom notification rules — fully under your control.</p>
        </div>
        <Button onClick={() => setEditing(emptyRule())} data-testid="automation-new-btn">
          <Plus className="w-4 h-4 mr-2" /> New Rule
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-rules">Rules ({rules.length})</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Run log ({runs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="rules">
          {rules.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
              No automations yet. Click "New Rule" to create your first one.
            </div>
          ) : (
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="text-left p-3 w-16">Active</th>
                    <th className="text-left p-3">Rule</th>
                    <th className="text-left p-3">Recipients</th>
                    <th className="text-left p-3">Channels</th>
                    <th className="text-left p-3">Recurrence</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <RuleRow
                      key={r.id} rule={r}
                      onEdit={setEditing}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                      onTest={openTest}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="runs">
          {runs.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
              No notifications fired yet.
            </div>
          ) : (
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">When</th>
                    <th className="text-left p-3">Rule</th>
                    <th className="text-left p-3">Order</th>
                    <th className="text-left p-3">Recipients</th>
                    <th className="text-left p-3">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-t border-border" data-testid={`run-row-${r.id}`}>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(r.fired_at).toLocaleString()}</td>
                      <td className="p-3 font-medium">{r.rule_name}</td>
                      <td className="p-3 font-mono text-xs">{r.order_id?.slice(0, 8)}…</td>
                      <td className="p-3 text-xs">
                        {(r.targets || []).map((t, i) => (
                          <span key={i} className={`inline-block mr-1 mb-1 px-1.5 py-0.5 rounded text-[10px] ${t.status === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            {t.channel}: {t.target}
                          </span>
                        ))}
                      </td>
                      <td className="p-3 text-xs">
                        <span className="text-emerald-700">{r.ok_count} ok</span>
                        {r.error_count > 0 && <span className="ml-2 text-red-700">{r.error_count} err</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {editing && (
        <RuleEditor
          rule={editing}
          placeholders={placeholders}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}

      {testDialog && (
        <Dialog open={true} onOpenChange={() => setTestDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Test fire — {testDialog.rule.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pick an order to use as test data. The rule will fire immediately, send notifications to the configured recipients, and be logged in the Run log.
              </p>
              <Select onValueChange={runTest}>
                <SelectTrigger data-testid="test-order-select"><SelectValue placeholder="Choose an order…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {orders.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.order_number || o.id?.slice(0, 8)} · {o.client_name || ''} · {o.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ===== RULE EDITOR =====
const RuleEditor = ({ rule: initial, placeholders, onSave, onClose }) => {
  const [rule, setRule] = useState(initial);
  const [activeTpl, setActiveTpl] = useState('fr');

  const set = (path, val) => {
    setRule((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const insertPlaceholder = (key) => {
    const fld = activeTpl === 'fr' ? 'body_fr' : 'body_en';
    set(`templates.${fld}`, (rule.templates?.[fld] || '') + `{${key}}`);
  };

  const addCondition = () => {
    const items = [...(rule.conditions?.items || []), { field: 'order.status', operator: 'eq', value: '' }];
    set('conditions.items', items);
  };
  const removeCondition = (idx) => {
    const items = (rule.conditions?.items || []).filter((_, i) => i !== idx);
    set('conditions.items', items);
  };
  const setCondField = (idx, field, val) => {
    const items = [...(rule.conditions?.items || [])];
    items[idx] = { ...items[idx], [field]: val };
    set('conditions.items', items);
  };

  const addRecipient = (type) => set('recipients', [...(rule.recipients || []), { type, value: '' }]);
  const removeRecipient = (idx) => set('recipients', rule.recipients.filter((_, i) => i !== idx));
  const setRecipientValue = (idx, val) => {
    const arr = [...rule.recipients];
    arr[idx] = { ...arr[idx], value: val };
    set('recipients', arr);
  };

  const toggleChannel = (c) => {
    const has = rule.channels.includes(c);
    set('channels', has ? rule.channels.filter((x) => x !== c) : [...rule.channels, c]);
  };

  const valid = useMemo(() => rule.name?.trim() && (rule.channels || []).length > 0 && (rule.recipients || []).length > 0, [rule]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rule-editor-dialog">
        <DialogHeader>
          <DialogTitle>{rule.id ? 'Edit rule' : 'New automation rule'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Name + enabled */}
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <Label>Rule name</Label>
              <Input value={rule.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Shipped + 45 days → email client" data-testid="rule-name-input" />
            </div>
            <div className="pt-7">
              <Switch checked={rule.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="rule-enabled-toggle" />
            </div>
          </div>

          {/* Trigger */}
          <div className="space-y-2">
            <Label className="font-semibold text-base">1. Trigger</Label>
            <Select value={rule.trigger.type} onValueChange={(v) => set('trigger', { type: v, config: defaultTriggerConfig(v) })}>
              <SelectTrigger data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="status_change">When order status changes</SelectItem>
                <SelectItem value="payment_state">When payment state changes</SelectItem>
                <SelectItem value="time_after">N days after a status (or order creation)</SelectItem>
                <SelectItem value="document_signed">When a document is signed</SelectItem>
                <SelectItem value="container_event">When container tracking event happens</SelectItem>
              </SelectContent>
            </Select>
            {rule.trigger.type === 'status_change' && (
              <div className="flex items-center gap-2">
                <Select value={rule.trigger.config.to_status} onValueChange={(v) => set('trigger.config.to_status', v)}>
                  <SelectTrigger className="flex-1" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {rule.trigger.type === 'payment_state' && (
              <div className="grid grid-cols-2 gap-2">
                <Select value={rule.trigger.config.state} onValueChange={(v) => set('trigger.config.state', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully_paid">Fully paid (100%)</SelectItem>
                    <SelectItem value="partial">Partial ≥ threshold</SelectItem>
                    <SelectItem value="balance_overdue">Balance overdue (&lt;100%)</SelectItem>
                  </SelectContent>
                </Select>
                {rule.trigger.config.state === 'partial' && (
                  <Input type="number" value={rule.trigger.config.threshold || 0} onChange={(e) => set('trigger.config.threshold', Number(e.target.value))} placeholder="Threshold %" />
                )}
              </div>
            )}
            {rule.trigger.type === 'time_after' && (
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" value={rule.trigger.config.days || 0} onChange={(e) => set('trigger.config.days', Number(e.target.value))} placeholder="Days" data-testid="time-days-input" />
                <Select value={rule.trigger.config.anchor} onValueChange={(v) => set('trigger.config.anchor', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status_change">after status =</SelectItem>
                    <SelectItem value="order_created">after order created</SelectItem>
                  </SelectContent>
                </Select>
                {rule.trigger.config.anchor === 'status_change' && (
                  <Select value={rule.trigger.config.anchor_status} onValueChange={(v) => set('trigger.config.anchor_status', v)}>
                    <SelectTrigger><SelectValue placeholder="status…" /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            {rule.trigger.type === 'document_signed' && (
              <div className="grid grid-cols-2 gap-2">
                <Select value={rule.trigger.config.document || 'any'} onValueChange={(v) => set('trigger.config.document', v)}>
                  <SelectTrigger data-testid="doc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any document</SelectItem>
                    <SelectItem value="avis">Avis de Versement</SelectItem>
                    <SelectItem value="bon_livraison">Bon de Livraison</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={rule.trigger.config.signed_by || 'any'} onValueChange={(v) => set('trigger.config.signed_by', v)}>
                  <SelectTrigger data-testid="signedby-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Signed by anyone</SelectItem>
                    <SelectItem value="client">Signed by client</SelectItem>
                    <SelectItem value="agent">Signed by agent</SelectItem>
                    <SelectItem value="both">Signed by both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {rule.trigger.type === 'container_event' && (
              <Select value={rule.trigger.config.t49_status || 'any'} onValueChange={(v) => set('trigger.config.t49_status', v)}>
                <SelectTrigger data-testid="t49-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any tracking event</SelectItem>
                  <SelectItem value="at_origin">At origin</SelectItem>
                  <SelectItem value="in_transit_to_port_of_loading">In transit to POL</SelectItem>
                  <SelectItem value="at_port_of_loading">At port of loading</SelectItem>
                  <SelectItem value="loaded_on_vessel">Loaded on vessel (departed)</SelectItem>
                  <SelectItem value="in_transit_to_port_of_discharge">In transit to POD</SelectItem>
                  <SelectItem value="at_port_of_discharge">Arrived at POD</SelectItem>
                  <SelectItem value="in_transit_to_final_destination">In transit to final dest.</SelectItem>
                  <SelectItem value="at_final_destination">At final destination</SelectItem>
                  <SelectItem value="delivered">Delivered (empty returned)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="font-semibold text-base">2. Conditions (extra filters)</Label>
              <div className="flex items-center gap-2">
                <Select value={rule.conditions?.op || 'AND'} onValueChange={(v) => set('conditions.op', v)}>
                  <SelectTrigger className="w-24" data-testid="cond-op-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="OR">OR</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={addCondition} data-testid="add-cond-btn">
                  <Plus className="w-3 h-3 mr-1" /> Condition
                </Button>
              </div>
            </div>
            {(rule.conditions?.items || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No extra conditions — rule fires whenever the trigger matches.</p>
            ) : (
              <div className="space-y-2">
                {rule.conditions.items.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`cond-row-${i}`}>
                    <Select value={c.field} onValueChange={(v) => setCondField(i, 'field', v)}>
                      <SelectTrigger className="col-span-5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={c.operator} onValueChange={(v) => setCondField(i, 'operator', v)}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="col-span-4" value={c.value} onChange={(e) => setCondField(i, 'value', e.target.value)} placeholder="value" />
                    <Button size="icon" variant="ghost" className="col-span-1 text-destructive" onClick={() => removeCondition(i)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recipients */}
          <div className="space-y-2">
            <Label className="font-semibold text-base">3. Recipients</Label>
            <div className="space-y-2">
              {(rule.recipients || []).map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`recipient-row-${i}`}>
                  <Select value={r.type} onValueChange={(v) => {
                    const arr = [...rule.recipients];
                    arr[i] = { type: v, value: '' };
                    set('recipients', arr);
                  }}>
                    <SelectTrigger className="col-span-5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client_email">Client — email</SelectItem>
                      <SelectItem value="client_telegram">Client — telegram</SelectItem>
                      <SelectItem value="assigned_agent">Assigned agent</SelectItem>
                      <SelectItem value="role">By role…</SelectItem>
                      <SelectItem value="custom_email">Custom email</SelectItem>
                    </SelectContent>
                  </Select>
                  {(r.type === 'role') && (
                    <Select value={r.value} onValueChange={(v) => setRecipientValue(i, v)}>
                      <SelectTrigger className="col-span-6"><SelectValue placeholder="role…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="logistics_manager">logistics_manager</SelectItem>
                        <SelectItem value="operations">operations</SelectItem>
                        <SelectItem value="accountant">accountant</SelectItem>
                        <SelectItem value="sales_agent">sales_agent</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {r.type === 'custom_email' && (
                    <Input className="col-span-6" value={r.value || ''} onChange={(e) => setRecipientValue(i, e.target.value)} placeholder="someone@example.com" />
                  )}
                  {(r.type === 'client_email' || r.type === 'client_telegram' || r.type === 'assigned_agent') && (
                    <span className="col-span-6 text-xs text-muted-foreground">Resolved from the order at fire time.</span>
                  )}
                  <Button size="icon" variant="ghost" className="col-span-1 text-destructive" onClick={() => removeRecipient(i)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => addRecipient('client_email')} data-testid="add-recipient-btn">
              <Plus className="w-3 h-3 mr-1" /> Add recipient
            </Button>
          </div>

          {/* Channels */}
          <div className="space-y-2">
            <Label className="font-semibold text-base">4. Channels</Label>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-3 py-1.5 border border-border cursor-pointer rounded">
                <input type="checkbox" checked={rule.channels.includes('email')} onChange={() => toggleChannel('email')} data-testid="channel-email-cb" />
                <span className="text-sm">Email (Resend)</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-1.5 border border-border cursor-pointer rounded">
                <input type="checkbox" checked={rule.channels.includes('telegram')} onChange={() => toggleChannel('telegram')} data-testid="channel-telegram-cb" />
                <span className="text-sm">Telegram</span>
              </label>
            </div>
          </div>

          {/* Templates */}
          <div className="space-y-2">
            <Label className="font-semibold text-base">5. Message templates</Label>
            <p className="text-xs text-muted-foreground">Click a placeholder below to insert into the body. Client receives the version matching their preferred language (default FR).</p>
            <Tabs value={activeTpl} onValueChange={setActiveTpl}>
              <TabsList>
                <TabsTrigger value="fr" data-testid="tab-tpl-fr">Français</TabsTrigger>
                <TabsTrigger value="en" data-testid="tab-tpl-en">English</TabsTrigger>
              </TabsList>
              <TabsContent value="fr" className="space-y-2 mt-2">
                <Input value={rule.templates.subject_fr || ''} onChange={(e) => set('templates.subject_fr', e.target.value)} placeholder="Sujet (email seulement)" />
                <Textarea rows={5} value={rule.templates.body_fr || ''} onChange={(e) => set('templates.body_fr', e.target.value)} placeholder="Bonjour {client_name}, votre véhicule {brand} {model} ..." data-testid="tpl-body-fr" />
              </TabsContent>
              <TabsContent value="en" className="space-y-2 mt-2">
                <Input value={rule.templates.subject_en || ''} onChange={(e) => set('templates.subject_en', e.target.value)} placeholder="Subject (email only)" />
                <Textarea rows={5} value={rule.templates.body_en || ''} onChange={(e) => set('templates.body_en', e.target.value)} placeholder="Hello {client_name}, your {brand} {model} ..." data-testid="tpl-body-en" />
              </TabsContent>
            </Tabs>
            <div className="flex flex-wrap gap-1 mt-2 bg-secondary/40 p-2 border border-border">
              {placeholders.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  className={`text-[10px] border border-border px-1.5 py-0.5 rounded hover:bg-primary hover:text-primary-foreground font-mono ${p.key === 'upload_link' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white'}`}
                  title={p.label}
                  data-testid={`placeholder-${p.key}`}
                >{`{${p.key}}`}</button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Tip: drop <span className="font-mono">{`{upload_link}`}</span> into the email body and the engine will auto-generate a fresh 14-day client document upload link each time the rule fires. Insert it into <b>both FR and EN</b> bodies so it works regardless of the client's language.
            </p>
          </div>

          {/* Recurrence */}
          <div className="space-y-2">
            <Label className="font-semibold text-base">6. Recurrence</Label>
            <div className="grid grid-cols-3 gap-2">
              <Select value={rule.recurrence.mode} onValueChange={(v) => set('recurrence.mode', v)}>
                <SelectTrigger data-testid="recurrence-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once per order</SelectItem>
                  <SelectItem value="recurring">Recurring every N days</SelectItem>
                </SelectContent>
              </Select>
              {rule.recurrence.mode === 'recurring' && (
                <Input type="number" value={rule.recurrence.interval_days} onChange={(e) => set('recurrence.interval_days', Number(e.target.value))} placeholder="days" />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-border">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave(rule)} disabled={!valid} data-testid="rule-save-btn">
              <ListChecks className="w-4 h-4 mr-2" /> Save rule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function defaultTriggerConfig(type) {
  if (type === 'status_change') return { to_status: 'shipped', any_change: false };
  if (type === 'payment_state') return { state: 'fully_paid', threshold: 50 };
  if (type === 'time_after') return { anchor: 'status_change', anchor_status: 'shipped', days: 45 };
  if (type === 'document_signed') return { document: 'any', signed_by: 'any' };
  if (type === 'container_event') return { t49_status: 'any' };
  return {};
}

export default AutomationsPage;
